import { useEffect } from "react";
import { Trophy, Star } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import confetti from "canvas-confetti";

interface AchievementToastProps {
  achievement: {
    name: string;
    description: string;
    icon: string;
    points: number;
    badge_color: string;
  };
  show: boolean;
}

export function AchievementToast({ achievement, show }: AchievementToastProps) {
  useEffect(() => {
    if (show) {
      // Disparar confetes
      fireConfetti(achievement.badge_color);
      
      toast({
        title: "🎉 Conquista Desbloqueada!",
        description: (
          <div className="space-y-2 animate-scale-in">
            <div className="flex items-start gap-3 p-3 rounded-lg border-2 transition-all duration-300"
              style={{ 
                borderColor: achievement.badge_color,
                background: `linear-gradient(135deg, ${achievement.badge_color}10, transparent)`,
              }}
            >
              <span className="text-3xl animate-bounce">{achievement.icon}</span>
              <div className="flex-1">
                <p className="font-bold text-base" style={{ color: achievement.badge_color }}>
                  {achievement.name}
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  {achievement.description}
                </p>
              </div>
              <Trophy className="h-5 w-5 text-yellow-500 animate-bounce" />
            </div>
            <div className="flex items-center justify-between px-3">
              <div className="flex items-center gap-1.5">
                <Star className="h-4 w-4 text-yellow-500 fill-yellow-500 animate-pulse" />
                <span className="text-sm font-bold text-yellow-600">+{achievement.points} pontos</span>
              </div>
              <Star className="h-4 w-4 text-primary animate-pulse" />
            </div>
          </div>
        ),
        duration: 6000,
        className: "border-2 shadow-2xl animate-scale-in",
      });
    }
  }, [show, achievement]);

  return null;
}

// Função para disparar confetes
const fireConfetti = (color: string) => {
  const count = 200;
  const defaults = {
    origin: { y: 0.7 },
    zIndex: 9999,
  };

  function fire(particleRatio: number, opts: confetti.Options) {
    confetti({
      ...defaults,
      ...opts,
      particleCount: Math.floor(count * particleRatio),
      colors: [color, '#FFD700', '#FFA500', '#FF6347', '#8A2BE2'],
    });
  }

  fire(0.25, {
    spread: 26,
    startVelocity: 55,
  });

  fire(0.2, {
    spread: 60,
  });

  fire(0.35, {
    spread: 100,
    decay: 0.91,
    scalar: 0.8,
  });

  fire(0.1, {
    spread: 120,
    startVelocity: 25,
    decay: 0.92,
    scalar: 1.2,
  });

  fire(0.1, {
    spread: 120,
    startVelocity: 45,
  });
};

// Hook personalizado para mostrar toast de achievement
export function useAchievementToast() {
  const showAchievementToast = (achievement: {
    name: string;
    description: string;
    icon: string;
    points: number;
    badge_color: string;
  }) => {
    // Disparar confetes
    fireConfetti(achievement.badge_color);

    // Toast com animação elaborada
    toast({
      title: "🎉 Conquista Desbloqueada!",
      description: (
        <div className="space-y-2 animate-scale-in">
          <div className="flex items-start gap-3 p-3 rounded-lg border-2 transition-all duration-300"
            style={{ 
              borderColor: achievement.badge_color,
              background: `linear-gradient(135deg, ${achievement.badge_color}10, transparent)`,
            }}
          >
            <span className="text-3xl animate-bounce">{achievement.icon}</span>
            <div className="flex-1">
              <p className="font-bold text-base" style={{ color: achievement.badge_color }}>
                {achievement.name}
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                {achievement.description}
              </p>
            </div>
            <Trophy className="h-5 w-5 text-yellow-500 animate-bounce" />
          </div>
          <div className="flex items-center justify-between px-3">
            <div className="flex items-center gap-1.5">
              <Star className="h-4 w-4 text-yellow-500 fill-yellow-500 animate-pulse" />
              <span className="text-sm font-bold text-yellow-600">+{achievement.points} pontos</span>
            </div>
            <Star className="h-4 w-4 text-primary animate-pulse" />
          </div>
        </div>
      ),
      duration: 6000,
      className: "border-2 shadow-2xl animate-scale-in",
    });
  };

  return { showAchievementToast };
}
